import type { LucideIcon } from "lucide-react";
import type { ComponentType } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/atoms/Button";
import { cn } from "@/utils/cn";

interface EmptyStateProps {
  /** Ícone simples (fallback), usado quando nenhuma ilustração é passada. */
  icon?: LucideIcon;
  /** Ilustração rica (ver @/components/atoms/illustrations) — preferida sempre
   *  que a tela precisar "transmitir futebol" mesmo sem dado nenhum. */
  illustration?: ComponentType<{ className?: string }>;
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  className?: string;
}

/**
 * EmptyState — reutilizado em toda a aplicação sempre que não houver dado
 * (grupos, jogos, notificações, participantes...). Especificação do guia de
 * marca: "Não deixe telas vazias. Sempre utilizar Empty States ilustrados."
 * Por isso favorece `illustration` (gráfico colorido em card) sobre o ícone
 * monocromático simples, que fica como fallback para casos pontuais.
 */
export function EmptyState({
  icon: Icon,
  illustration: Illustration,
  title,
  description,
  actionLabel,
  onAction,
  secondaryActionLabel,
  onSecondaryAction,
  className,
}: EmptyStateProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className={cn(
        "flex flex-col items-center justify-center gap-4 rounded-2xl border border-border bg-card px-6 py-8 text-center shadow-soft",
        className
      )}
    >
      {Illustration ? (
        <Illustration className="h-32 w-full max-w-[220px] rounded-xl" />
      ) : Icon ? (
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-50 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
          <Icon className="h-7 w-7" strokeWidth={1.75} />
        </div>
      ) : null}

      <div className="space-y-1">
        <p className="font-display text-base font-semibold">{title}</p>
        {description && <p className="max-w-xs text-sm text-muted-foreground">{description}</p>}
      </div>

      {(actionLabel || secondaryActionLabel) && (
        <div className="flex flex-wrap items-center justify-center gap-2">
          {actionLabel && onAction && (
            <Button variant="primary" size="sm" onClick={onAction}>
              {actionLabel}
            </Button>
          )}
          {secondaryActionLabel && onSecondaryAction && (
            <Button variant="outline" size="sm" onClick={onSecondaryAction}>
              {secondaryActionLabel}
            </Button>
          )}
        </div>
      )}
    </motion.div>
  );
}
